import Link from "next/link";

// Define prop types for the Button component
type Props = {
  href?: string;
  text: string;
  icon?: React.ReactNode;
  // filled?: Boolean;
  filledColor?: string;
  outline?: boolean;
  textColor?: string;
  outlineColor?: string;
  rounded?: boolean;
  fontSize?: string;
  fontWeight?: string;
  width?: string;
  align?: string;
  showHover?: boolean;
  paddingX?: string;
  paddingY?: string;
  gap?: string;
  filledBorderColor?: string;
  filledBorderWidth?: string;
  onClick?: () => void;
  hover?: string;
  btnClassName?: string;
  disabled?: boolean;
  cursor?: string;
};

// Extract reusable class generation logic
const getButtonClasses = ({
  outline,
  width,
  gap,
  paddingX,
  paddingY,
  fontSize,
  fontWeight,
  color,
  rounded,
  hoverEffect,
  disabled,
}: {
  outline: boolean;
  width: string;
  gap: string;
  paddingX: string;
  paddingY: string;
  fontSize: string;
  fontWeight: string;
  color: string;
  rounded: boolean;
  hoverEffect: string;
  disabled: boolean;
}) => {
  return `button flex ${width} ${gap} ${paddingX} ${paddingY} ${fontSize} ${fontWeight} items-center ${color} ${
    rounded ? "rounded-full" : "rounded-md"
  } ${hoverEffect} ${disabled ? "cursor-default" : "cursor-pointer"}`;
};

// Define the Button component
export default function Button({
  href,
  text,
  icon,
  outline = false,
  filledColor = "bg-primary",
  textColor = "text-white",
  outlineColor = "border-primary",
  rounded = false,
  fontSize = "text-sm",
  fontWeight = "font-normal",
  width = "min-w-max",
  align = "text-left",
  showHover = true,
  paddingX = "px-2",
  paddingY = "py-4",
  gap = "",
  filledBorderWidth = "border-0",
  filledBorderColor = "border-transparent",
  hover = "hover:text-white",
  onClick,
  btnClassName = "w-fit",
  disabled = false,
}: Readonly<Props>) {
  const hoverEffect = showHover ? `${hover} ease-in-out` : "";
  const color = outline
    ? `border ${outlineColor} ${textColor}`
    : `${filledColor} ${textColor} ${filledBorderColor} ${filledBorderWidth}`;

  const ButtonContent = (
    <div
      className={getButtonClasses({
        outline,
        width,
        gap,
        paddingX,
        paddingY,
        fontSize,
        fontWeight,
        color,
        rounded,
        hoverEffect,
        disabled,
      })}
    >
      {icon && <div className={outline ? "" : "mr-2"}>{icon}</div>}
      <div className={`flex-1 text-nowrap ${align}`}>{text}</div>
    </div>
  );

  if (href) {
    return <Link href={href}>{ButtonContent}</Link>;
  }

  return (
    <button
      name={text}
      aria-label={`${text}_button`}
      onClick={disabled ? undefined : onClick}
      className={`${btnClassName} ${
        disabled ? "cursor-default" : "cursor-pointer"
      }`}
      disabled={disabled}
    >
      {ButtonContent}
    </button>
  );
}
